import { call, put, select, takeEvery } from 'redux-saga/effects';
import { Brand, Group, Site } from 'sections/client-management/entities';
import {
  Actions as BrandActions,
  client as BrandClient,
  Selectors as BrandSelectors,
} from 'sections/client-management/services/brands';
import {
  Actions as GroupActions,
  client as GroupClient,
  Selectors as GroupSelectors,
} from 'sections/client-management/services/groups';
import { Actions as SiteActions, client as SiteClient } from 'sections/client-management/services/sites';
import { EntityWithId, IClient } from 'services/genericAPIGatewayService';
import {
  NEW_BRAND_TO_MOVE_TO,
  NEW_GROUP_TO_MOVE_TO,
} from '../../ui/pages/client-settings/utils/ClientSettingsConstants';

enum Types {
  CreateAndMove = 'MOVE_CLIENT:CREATE_AND_MOVE',
}

export interface Action {
  type: Types.CreateAndMove;
  payload: ActionPayload;
}

export interface ActionPayload {
  oldGroupId: string;
  oldBrandId: string;
  groupId: string;
  brandId: string;
  siteId: string;
  isSite: boolean;
}

export const Actions = {
  createAndMove: (
    oldGroupId: string,
    oldBrandId: string,
    groupId: string,
    brandId: string,
    siteId: string,
    isSite: boolean,
  ): Action => ({
    type: Types.CreateAndMove,
    payload: {
      groupId,
      brandId,
      oldBrandId,
      oldGroupId,
      siteId,
      isSite,
    },
  }),
};

/**
  @deprecated - https://github.com/SaleCycle/my-salecycle-com#legacy-code 
*/
export function* moveAndCreateClientSaga(
  payload: ActionPayload,
  groupClient: IClient<Group>,
  brandClient: IClient<Brand>,
  siteClient: IClient<Site>,
) {
  // 1. CREATE STEP
  const oldGroupId = payload.oldGroupId;
  const oldBrandId = payload.oldBrandId;
  let groupId = payload.groupId;
  let brandId = payload.brandId;
  const siteId = payload.siteId;
  const isSite = payload.isSite;

  if (groupId === NEW_GROUP_TO_MOVE_TO) {
    // Create group if needed
    yield put(GroupActions.startCreate(groupId));

    const group = yield select(GroupSelectors.getUpdatedEntity, groupId);
    try {
      const savedGroup: EntityWithId<Group> = yield call([groupClient, 'post'], `/groups`, group);
      yield put(GroupActions.completeCreate(groupId, savedGroup));
      groupId = savedGroup.id;
    } catch (error) {
      return yield put(GroupActions.failCreate(groupId, (error as Error).message));
    }
  }

  if (brandId === NEW_BRAND_TO_MOVE_TO) {
    // Create brand if needed
    yield put(BrandActions.startCreate(brandId));

    const brand = yield select(BrandSelectors.getUpdatedEntity, brandId);
    try {
      const savedBrand: EntityWithId<Brand> = yield call([brandClient, 'post'], `/brands`, {
        ...brand,
        groupId,
      });
      yield put(BrandActions.completeCreate(brandId, savedBrand));
      brandId = savedBrand.id;
    } catch (error) {
      return yield put(BrandActions.failCreate(brandId, (error as Error).message));
    }
  }

  // move brand
  if (groupId && oldGroupId && brandId && !isSite) {
    yield put(BrandActions.startUpdate(brandId));
    try {
      const movedBrand: EntityWithId<Brand> = yield call([brandClient, 'put'], `/brands/${brandId}/move`, {
        fromId: payload.oldGroupId,
        toId: groupId,
      });
      yield put(BrandActions.completeUpdate(movedBrand));
    } catch (error) {
      return yield put(BrandActions.failUpdate(brandId, (error as Error).message));
    }
  }

  // move site
  if (brandId && siteId && oldBrandId && isSite) {
    yield put(SiteActions.startUpdate(siteId));
    try {
      const movedSite: EntityWithId<Site> = yield call([siteClient, 'put'], `/sites/${siteId}/move`, {
        fromId: payload.oldBrandId,
        toId: brandId,
      });
      yield put(SiteActions.completeUpdate(movedSite));
    } catch (error) {
      return yield put(SiteActions.failUpdate(siteId, (error as Error).message));
    }
  }
}

/**
  @deprecated - https://github.com/SaleCycle/my-salecycle-com#legacy-code 
*/
export function* moveListener() {
  yield takeEvery(Types.CreateAndMove, (action: Action) =>
    moveAndCreateClientSaga(action.payload, GroupClient, BrandClient, SiteClient),
  );
}
